<template>
  <div class="labs-rawmaterials">
    <div class="labs-section-header">
      <h3>Raw Materials</h3>
      <div class="labs-header-actions">
        <input
          type="text"
          v-model="searchText"
          placeholder="Search materials..."
          class="labs-search-input"
        />
      </div>
    </div>

    <div v-if="loading" class="labs-loading">Loading raw materials...</div>

    <div v-else-if="filteredMaterials.length === 0" class="labs-empty">
      No raw materials found.
    </div>

    <div v-else class="labs-table-wrapper">
      <table class="labs-table">
        <thead>
          <tr>
            <th class="col-visible">Visible</th>
            <th class="col-tracking">Tracking #</th>
            <th class="col-desc">Description</th>
            <th class="col-binder">Binder</th>
            <th class="col-weight">Weight</th>
            <th class="col-volume">Volume</th>
            <th class="col-density">Bulk Density (kg/L)</th>
            <th class="col-wbm">WBM Avg 5%</th>
            <th class="col-storage">Storage</th>
            <th class="col-sds">SDS</th>
            <th class="col-comments">Comments</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="mat in paginatedMaterials"
            :key="mat.id"
            :class="{ 'row-hidden': !mat.visibleToCustomer }"
          >
            <td class="col-visible">
              <label class="labs-toggle" v-if="canToggleVisibility">
                <input
                  type="checkbox"
                  :checked="mat.visibleToCustomer"
                  @change="toggleVisibility(mat)"
                />
                <span class="labs-toggle-slider"></span>
              </label>
              <span v-else class="labs-visibility-badge" :class="mat.visibleToCustomer ? 'visible' : 'hidden'">
                {{ mat.visibleToCustomer ? 'Yes' : 'No' }}
              </span>
            </td>
            <td class="col-tracking">{{ mat.materialTrackingNumber || '—' }}</td>
            <td class="col-desc">
              <span class="labs-desc-text" :title="mat.materialDescription">
                {{ truncate(mat.materialDescription, 40) }}
              </span>
            </td>
            <td class="col-binder">
              <span v-if="mat.binder" class="labs-binder-badge">Binder</span>
              <span v-else>—</span>
            </td>
            <td class="col-weight">
              {{ formatDecimal(mat.weight) }}
              <span v-if="mat.weightUnit" class="unit-label">{{ mat.weightUnit }}</span>
            </td>
            <td class="col-volume">
              {{ formatDecimal(mat.volume) }}
              <span v-if="mat.volumeUnit" class="unit-label">{{ mat.volumeUnit }}</span>
            </td>
            <td class="col-density">{{ formatDecimal(mat.bulkDensityKgL) }}</td>
            <td class="col-wbm">{{ formatDecimal(mat.wbmAvg5Pct) }}</td>
            <td class="col-storage">{{ mat.storageType || '—' }}</td>
            <td class="col-sds">
              <a
                v-if="mat.sdsPath"
                :href="mat.sdsPath"
                target="_blank"
                class="labs-sds-link"
                :title="mat.sdsOriginalName"
              >
                SDS
              </a>
              <span v-else>—</span>
            </td>
            <td class="col-comments">
              <span class="labs-desc-text" :title="mat.comments">
                {{ truncate(mat.comments, 30) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div class="labs-pagination" v-if="totalPages > 1">
        <button @click="currentPage = 1" :disabled="currentPage === 1">&laquo;</button>
        <button @click="currentPage--" :disabled="currentPage === 1">&lsaquo;</button>
        <span class="labs-page-info">Page {{ currentPage }} of {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage === totalPages">&rsaquo;</button>
        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages">&raquo;</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RawMaterials',
  props: {
    labsCustomerId: { type: Number, required: true },
    apiBase: { type: String, required: true },
    canToggleVisibility: { type: Boolean, default: false }
  },
  data() {
    return {
      materials: [],
      searchText: '',
      loading: false,
      currentPage: 1,
      pageSize: 20
    };
  },
  computed: {
    filteredMaterials() {
      if (!this.searchText.trim()) return this.materials;
      const q = this.searchText.toLowerCase();
      return this.materials.filter(m =>
        (m.materialTrackingNumber && m.materialTrackingNumber.toLowerCase().includes(q)) ||
        (m.materialDescription && m.materialDescription.toLowerCase().includes(q)) ||
        (m.storageType && m.storageType.toLowerCase().includes(q)) ||
        (m.binderTags && m.binderTags.toLowerCase().includes(q)) ||
        (m.comments && m.comments.toLowerCase().includes(q))
      );
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredMaterials.length / this.pageSize));
    },
    paginatedMaterials() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredMaterials.slice(start, start + this.pageSize);
    }
  },
  watch: {
    labsCustomerId: 'loadMaterials',
    searchText() { this.currentPage = 1; }
  },
  created() {
    this.loadMaterials();
  },
  methods: {
    async loadMaterials() {
      if (!this.labsCustomerId) return;
      this.loading = true;
      try {
        const resp = await axios.get(
          `${this.apiBase}/api/labs/customers/${this.labsCustomerId}/rawmaterials`
        );
        this.materials = resp.data || [];
      } catch (err) {
        console.error('Failed to load raw materials:', err);
        this.materials = [];
      } finally {
        this.loading = false;
      }
    },

    async toggleVisibility(mat) {
      const newVal = !mat.visibleToCustomer;
      try {
        await axios.patch(
          `${this.apiBase}/api/labs/rawmaterials/${mat.id}/visibility`,
          { visible: newVal }
        );
        mat.visibleToCustomer = newVal;
      } catch (err) {
        console.error('Failed to toggle raw material visibility:', err);
      }
    },

    formatDate(val) {
      if (!val) return '—';
      return new Date(val).toLocaleDateString();
    },

    formatDecimal(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 });
    },

    truncate(text, max) {
      if (!text) return '—';
      return text.length > max ? text.substring(0, max) + '...' : text;
    }
  }
};
</script>

<style>
/* RawMaterials — non-scoped so styles survive builds */
.labs-rawmaterials .labs-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.labs-rawmaterials .labs-section-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #1a1a1a;
}
.labs-rawmaterials .labs-search-input {
  padding: 6px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.875rem;
  width: 220px;
}
.labs-rawmaterials .labs-table-wrapper {
  overflow-x: auto;
}
.labs-rawmaterials .labs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.labs-rawmaterials .labs-table th,
.labs-rawmaterials .labs-table td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e5e5e5;
  white-space: nowrap;
}
.labs-rawmaterials .labs-table thead th {
  background: #f5f6f8;
  font-weight: 600;
  color: #555;
  position: sticky;
  top: 0;
  z-index: 1;
}
.labs-rawmaterials .labs-table tbody tr:hover {
  background: #f9fafb;
}
.labs-rawmaterials .row-hidden {
  opacity: 0.55;
}
.labs-rawmaterials .labs-binder-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #cce5ff;
  color: #004085;
}
.labs-rawmaterials .labs-desc-text {
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.labs-rawmaterials .labs-sds-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.8rem;
}
.labs-rawmaterials .labs-sds-link:hover {
  text-decoration: underline;
}
.labs-rawmaterials .unit-label {
  color: #888;
  font-size: 0.75rem;
  margin-left: 2px;
}

/* Toggle switch */
.labs-rawmaterials .labs-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
}
.labs-rawmaterials .labs-toggle input { opacity: 0; width: 0; height: 0; }
.labs-rawmaterials .labs-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 20px;
  transition: 0.2s;
}
.labs-rawmaterials .labs-toggle-slider::before {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  left: 2px;
  bottom: 2px;
  background: #fff;
  border-radius: 50%;
  transition: 0.2s;
}
.labs-rawmaterials .labs-toggle input:checked + .labs-toggle-slider {
  background: #28a745;
}
.labs-rawmaterials .labs-toggle input:checked + .labs-toggle-slider::before {
  transform: translateX(16px);
}
.labs-rawmaterials .labs-visibility-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
}
.labs-rawmaterials .labs-visibility-badge.visible { background: #d4edda; color: #155724; }
.labs-rawmaterials .labs-visibility-badge.hidden  { background: #e9ecef; color: #6c757d; }

/* Pagination */
.labs-rawmaterials .labs-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 0;
}
.labs-rawmaterials .labs-pagination button {
  background: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 0.85rem;
  color: #333;
}
.labs-rawmaterials .labs-pagination button:disabled {
  opacity: 0.4;
  cursor: default;
}
.labs-rawmaterials .labs-pagination button:not(:disabled):hover {
  background: #f0f0f0;
}
.labs-rawmaterials .labs-page-info {
  font-size: 0.85rem;
  color: #555;
  margin: 0 8px;
}

.labs-rawmaterials .labs-loading,
.labs-rawmaterials .labs-empty {
  padding: 24px;
  text-align: center;
  color: #888;
}
</style>
